import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { useTheme } from "./context/ThemeContext";

// Leaflet CSS - IMPORTANT!
import "leaflet/dist/leaflet.css";

// Layout
import Layout from "./components/layout/Layout";

// Components
import ScrollToTop from "./components/common/ScrollToTop";
import ClickSpark from "./components/common/ClickSpark";

// Pages
import HomePage from "./pages/HomePage";
import ContactPage from "./pages/ContactPage";
import GolfRSANewsPage from "./pages/GolfRSANewsPage";
import ClubsPage from "./pages/ClubsPage";
import OrderOfMeritPage from "./pages/OrderOfMeritPage";
import FixturesPage from "./pages/FixturesPage";
import ResultsPage from "./pages/ResultsPage";
import ResultDetailPage from "./pages/ResultDetailPage";
import ExecutiveCommitteePage from "./pages/ExecutiveCommitteePage";

// Tournaments
import TournamentsPage from "./tournaments/TournamentsPage";
import TournamentDetailPage from "./tournaments/TournamentDetailPage";

// News
import NewsPage from "./pages/NewsPage";
import NewsDetailPage from "./pages/NewsDetailPage";

// Admin
import AdminLogin from "./admin/AdminLogin";
import AdminDashboard from "./admin/AdminDashboard";
import AdminCreateUser from "./admin/AdminCreateUser";
import AdminManageUsers from "./admin/AdminManageUsers";
import AdminTournaments from "./admin/AdminTournaments";
import AdminTournamentEditor from "./admin/AdminTournamentEditor";
import AdminTournamentView from "./admin/AdminTournamentView";
import AdminTournamentRegistrations from "./admin/AdminTournamentRegistrations";
import AdminPendingRegistrations from "./admin/AdminPendingRegistrations";
import AdminClubs from "./admin/AdminClubs";
import AdminClubEditor from "./admin/AdminClubEditor";
import AdminLayout from "./admin/AdminLayout";
import ProtectedRoute from "./admin/ProtectedRoute";
import AdminPosts from "./admin/AdminPosts";
import AdminPostEditor from "./admin/AdminPostEditor";
import AdminCategories from "./admin/AdminCategories";
import AdminResults from "./admin/AdminResults";
import AdminResultEditor from "./admin/AdminResultEditor";
import AdminOOM from "./admin/oom/AdminOOM";

// Component to handle ClickSpark enablement based on route
const ClickSparkController = () => {
  const location = useLocation();

  useEffect(() => {
    // Disable ClickSpark on admin pages for performance
    if (location.pathname.startsWith("/admin")) {
      document.body.classList.remove("enable-clickspark");
    } else {
      // Enable ClickSpark on public pages
      document.body.classList.add("enable-clickspark");
    }

    // Cleanup
    return () => {
      document.body.classList.remove("enable-clickspark");
    };
  }, [location]);

  return null;
};

function App() {
  const { isDarkMode } = useTheme();

  return (
    <Router>
      <ScrollToTop />
      <ClickSparkController />
      <ClickSpark
        sparkColor={isDarkMode ? "#66BB6A" : "#4CAF50"}
        sparkSize={12}
        sparkRadius={20}
        sparkCount={8}
        duration={500}
        easing="ease-out"
      >
        <div
          className={`App min-h-screen transition-colors duration-300 ${
            isDarkMode
              ? "bg-fsgu-gray-900 text-fsgu-silver-100"
              : "bg-fsgu-white text-fsgu-silver-900"
          }`}
        >
          <Routes>
            {/* Public Routes with Main Layout */}
            <Route
              path="/"
              element={
                <Layout>
                  <HomePage />
                </Layout>
              }
            />
            <Route
              path="/contact"
              element={
                <Layout>
                  <ContactPage />
                </Layout>
              }
            />

            {/* Tournament Public Pages */}
            <Route
              path="/tournaments"
              element={
                <Layout>
                  <TournamentsPage />
                </Layout>
              }
            />
            <Route
              path="/tournaments/:id"
              element={
                <Layout>
                  <TournamentDetailPage />
                </Layout>
              }
            />

            <Route
              path="/about/executive"
              element={
                <Layout>
                  <ExecutiveCommitteePage />
                </Layout>
              }
            />

            <Route
              path="/results"
              element={
                <Layout>
                  <ResultsPage />
                </Layout>
              }
            />
            <Route
              path="/results/:slug"
              element={
                <Layout>
                  <ResultDetailPage />
                </Layout>
              }
            />

            {/* Admin Login - No Layout, No ClickSpark */}
            <Route path="/admin/login" element={<AdminLogin />} />

            {/* Protected Admin Routes - Admin Layout, No ClickSpark */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminDashboard />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            {/* User Management */}
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminManageUsers />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/users/create"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminCreateUser />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            {/* Fixtures Page */}
            <Route
              path="/fixtures"
              element={
                <Layout>
                  <FixturesPage />
                </Layout>
              }
            />

            {/* Club Management */}
            <Route
              path="/admin/clubs"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminClubs />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/clubs/add"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminClubEditor />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/clubs/edit/:id"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminClubEditor />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            {/* News Management */}
            <Route
              path="/admin/news"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminPosts />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/news/add"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminPostEditor />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/news/edit/:id"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminPostEditor />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/news/categories"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminCategories />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            {/* Results Management */}
            <Route
              path="/admin/results"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminResults />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/results/add"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminResultEditor />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/results/edit/:id"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminResultEditor />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/oom"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminOOM />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            {/* Tournament Management */}
            <Route
              path="/admin/tournaments"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminTournaments />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/tournaments/add"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminTournamentEditor />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/tournaments/:id"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminTournamentView />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/pending"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminPendingRegistrations />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/tournaments/edit/:id"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminTournamentEditor />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/tournaments/:tournamentId/registrations"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminTournamentRegistrations />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            {/* Public Pages */}
            <Route
              path="/golfRSA-news"
              element={
                <Layout>
                  <GolfRSANewsPage />
                </Layout>
              }
            />
            <Route
              path="/clubs"
              element={
                <Layout>
                  <ClubsPage />
                </Layout>
              }
            />
            <Route
              path="/order-of-merit"
              element={
                <Layout>
                  <OrderOfMeritPage />
                </Layout>
              }
            />

            <Route
              path="/news"
              element={
                <Layout>
                  <NewsPage />
                </Layout>
              }
            />
            <Route
              path="/news/:slug"
              element={
                <Layout>
                  <NewsDetailPage />
                </Layout>
              }
            />

            {/* 404 - Catch all */}
            <Route
              path="*"
              element={
                <Layout>
                  <NotFound />
                </Layout>
              }
            />
          </Routes>
        </div>
      </ClickSpark>
    </Router>
  );
}

// 404 Not Found Component
const NotFound = () => {
  const { isDarkMode } = useTheme();

  return (
    <div className="section min-h-[60vh] flex items-center justify-center">
      <div className="container-custom text-center">
        <div className="inline-block mb-6">
          <img
            src="/fsgu-logo.png"
            alt="FSGU Logo"
            className="h-20 w-auto mx-auto mb-4 opacity-50"
          />
        </div>

        <h1
          className={`text-4xl sm:text-5xl font-bold mb-4 ${
            isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
          }`}
        >
          404 - Page Not Found
        </h1>

        <p
          className={`text-lg mb-8 max-w-md mx-auto ${
            isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"
          }`}
        >
          The page you're looking for seems to be out of bounds. Let's get you
          back on the fairway.
        </p>

        <a href="/" className="btn btn-primary">
          Return to Home
        </a>
      </div>
    </div>
  );
};

export default App;
